import { useEffect, useMemo, useRef, useState } from 'react'

import ScanGlassSelect from './ScanGlassSelect'
import ScanImageCarousel from './ScanImageCarousel'
import {
  IconBarcode,
  IconCameraCheck,
  IconCameraRetake,
  IconCartPlus,
  IconImageOff,
  IconImagePlus,
  IconLink,
  IconPriceTag,
  IconSearchRefresh,
  ScanIconButton,
} from './ScanIcons'
import ScanMenu from './ScanMenu'
import ScanStorePicker from './ScanStorePicker'
import ScanSuggestionList from './ScanSuggestionList'
import ScanCropEditor from './ScanCropEditor'
import { persistImageUrl } from '../../utils/itemMeta'
import { normalizeImageCandidates } from '../../utils/imageCandidates'
import { displayProductName, isUsableProductName } from '../../utils/productName'

const TAX_OPTIONS = [
  { value: '', label: 'Non déterminée' },
  { value: 'included', label: 'Taxe incluse' },
  { value: 'excluded', label: 'Taxe non incluse' },
]

const CURRENCY_OPTIONS = [
  { value: 'JPY', label: 'JPY · ¥' },
  { value: 'EUR', label: 'EUR · €' },
]

function parsePrice(value) {
  return Number(String(value ?? '').replace(/[¥€円,\s]/g, '').replace(',', '.'))
}

function frenchName(item = {}) {
  return item.name_fr || item.translated_name || item.translatedName || item.name || item.suggestedName || ''
}

function candidateImage(candidate) {
  return candidate.official_image_url
    || candidate.image
    || candidate.officialImage?.url
    || candidate.officialImage
    || ''
}

export default function ScanReview({
  draft,
  onChange,
  onRetake,
  onSubmit,
  submitting,
  error,
  storeState,
  onAdditionalPhoto,
  onRejectSuggestion,
  onSearchOfficialImage,
  onImportOfficialImage,
  onOfficialImageUrl,
  onUseCaptureAsOfficial,
  onOfficialDisplayError,
  searchingOfficial,
  onCropChange,
  onRecrop,
  recroping,
}) {
  const priceRef = useRef(null)
  const importRef = useRef(null)
  const urlRef = useRef(null)
  const [showUrlField, setShowUrlField] = useState(false)
  const currency = draft.currency === 'EUR' ? 'EUR' : 'JPY'
  const suggestions = useMemo(
    () => (draft.alternatives || [])
      .map((candidate, index) => (
        typeof candidate === 'string'
          ? { id: `${candidate}-${index}`, name_fr: candidate }
          : {
              ...candidate,
              name_fr: frenchName(candidate),
              id: candidate.id || `${frenchName(candidate) || 'candidate'}-${index}`,
            }
      ))
      .filter((candidate) => isUsableProductName(candidate.name_fr))
      .slice(0, 3),
    [draft.alternatives],
  )
  const officialImages = useMemo(
    () => normalizeImageCandidates(draft.officialImages || [])
      .filter((item) => !(draft.failedOfficialImages || []).includes(item.url)),
    [draft.officialImages, draft.failedOfficialImages],
  )
  const selectedOfficial = persistImageUrl(draft.officialImage) || officialImages[0]?.url || ''
  const hasOfficial = officialImages.length > 0
  const capturePreview = persistImageUrl(draft.captureImageUrl) || draft.previewUrl || ''
  const galleryImages = useMemo(() => {
    const captureSlide = capturePreview ? [{
      url: capturePreview,
      source_kind: 'capture',
      source_name: 'Photographie originale',
    }] : []
    return [
      ...officialImages,
      ...captureSlide.filter((slide) => !officialImages.some((item) => item.url === slide.url)),
    ]
  }, [officialImages, capturePreview])
  const imageSearchError = draft.imageSearchError || ''
  const missingImageLabel = imageSearchError === 'display_failed' || imageSearchError === 'download_failed'
    ? 'Une image commerciale a été trouvée mais n’a pas pu être affichée.'
    : imageSearchError === 'search_unavailable'
      ? 'La recherche d’image commerciale est indisponible.'
      : 'Aucune correspondance visuelle identifiée'
  const validPrice = parsePrice(draft.price) > 0
  const canSubmit = Boolean(
    String(draft.name || '').trim()
    && validPrice
    && (currency === 'JPY' || currency === 'EUR'),
  )

  useEffect(() => {
    if (!draft.price) priceRef.current?.focus()
  }, [draft.price])

  const chooseSuggestion = (candidate) => {
    const nextImages = normalizeImageCandidates(
      candidate.official_images || candidate.officialImages || draft.officialImages,
    )
    const nextOfficial = persistImageUrl(candidateImage(candidate)) || nextImages[0]?.url || ''
    onChange({
      name: frenchName(candidate),
      translatedName: frenchName(candidate),
      originalName: candidate.ocr_raw || candidate.nameJa || draft.originalName,
      brand: candidate.brand || '',
      model: candidate.model_fr || candidate.model || '',
      variant: candidate.variant_fr || candidate.variant || '',
      category: candidate.category_fr || candidate.category || '',
      generation: candidate.generation || '',
      color: candidate.color || draft.color,
      nameEn: candidate.nameEn || candidate.name_en || candidate.canonical_name_international || '',
      barcode: candidate.barcode || draft.barcode,
      officialImage: nextOfficial,
      officialImages: nextImages,
      officialImageMeta: nextOfficial ? {
        url: nextOfficial,
        source_url: candidate.image_source_url || '',
        source_name: candidate.image_source_name || '',
      } : null,
      confidence: candidate.identification_confidence ?? candidate.confidence ?? draft.confidence,
      captureImageUrl: draft.captureImageUrl,
    })
    if (!nextOfficial) onSearchOfficialImage?.(candidate)
  }

  const submitOfficialUrl = () => {
    const value = urlRef.current?.value?.trim()
    if (value) onOfficialImageUrl?.(value)
  }

  const changeCurrency = (nextCurrency) => {
    if (nextCurrency === currency) return
    onChange({
      currency: nextCurrency,
      price: '',
      convertedPrice: null,
    })
  }

  return (
    <>
      <div className="scan-body">
        <section className="scan-result-block" aria-labelledby="scan-result-title">
          <span className="scan-result-kicker">
            {draft.identificationStatus === 'provisional'
              ? 'Identification provisoire'
              : draft.identificationStatus === 'reverse_search'
                ? 'Correspondance visuelle'
                : 'Suggestion descriptive'}
          </span>
          <h3 id="scan-result-title">{displayProductName(draft.name)}</h3>
          <p>
            {[draft.brand, draft.model, draft.generation, draft.variant, draft.commercialRef].filter(Boolean).join(' · ')
              || 'Complétez le modèle ou la variante si nécessaire.'}
          </p>
        </section>

        {draft.previewUrl || draft.captureImageUrl ? (
          <ScanCropEditor
            src={draft.previewUrl || draft.captureImageUrl}
            box={draft.productBox}
            onChange={(productBox) => onCropChange?.(productBox)}
            onConfirm={(productBox) => onRecrop?.(productBox)}
            confirming={recroping}
          />
        ) : null}

        {officialImages.length ? (
          <ScanImageCarousel
            images={galleryImages}
            activeUrl={selectedOfficial}
            onSelect={(slide) => onChange({
              officialImage: slide.url,
              officialImageMeta: {
                url: slide.url,
                source_url: slide.source_url || '',
                source_name: slide.source_name || '',
              },
              officialImages,
              captureImageUrl: draft.captureImageUrl,
            })}
            onDisplayError={(url) => onOfficialDisplayError?.(url)}
          />
        ) : searchingOfficial ? (
          <div className="scan-image-skeleton" role="status" aria-live="polite">
            <span className="scan-image-skeleton-shimmer" aria-hidden="true" />
            <span>Recherche d’images…</span>
          </div>
        ) : capturePreview ? (
          <div className="scan-preview-single">
            <img className="scan-review-photo" src={capturePreview} alt="" />
          </div>
        ) : (
          <div className="scan-suggestion-placeholder" aria-hidden="true">?</div>
        )}

        {!hasOfficial && !searchingOfficial ? (
          <p className="scan-image-missing" role="status">
            <IconImageOff />
            <span>{missingImageLabel}</span>
          </p>
        ) : null}

        <div className="scan-image-actions">
          <ScanMenu
            label="Modifier l’image"
            items={[
              {
                label: searchingOfficial ? 'Recherche en cours…' : 'Nouvelle recherche d’images',
                icon: <IconSearchRefresh />,
                disabled: searchingOfficial,
                onClick: onSearchOfficialImage,
              },
              {
                label: 'Importer une image',
                icon: <IconImagePlus />,
                onClick: () => importRef.current?.click(),
              },
              {
                label: 'Saisir une URL d’image',
                icon: <IconLink />,
                onClick: () => setShowUrlField(true),
              },
              draft.previewUrl || draft.captureImageUrl ? {
                label: 'Utiliser la photographie prise',
                icon: <IconCameraCheck />,
                onClick: onUseCaptureAsOfficial,
              } : null,
            ]}
          />
          <input
            ref={importRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (file) onImportOfficialImage?.(file)
            }}
          />
          {showUrlField ? (
            <div className="scan-url-row">
              <input
                ref={urlRef}
                className="scan-url-input"
                placeholder="https://…"
                inputMode="url"
                aria-label="URL d’image produit"
              />
              <button type="button" className="scan-secondary" onClick={submitOfficialUrl}>
                Utiliser cette URL
              </button>
            </div>
          ) : null}
        </div>

        {suggestions.length ? (
          <section className="scan-suggestion-section" aria-labelledby="scan-suggestions-title">
            <div className="scan-section-heading">
              <h3 id="scan-suggestions-title">Autres objets possibles</h3>
              <span>{suggestions.length}/3</span>
            </div>
            <ScanSuggestionList
              suggestions={suggestions}
              previewUrl={draft.previewUrl}
              onSelect={chooseSuggestion}
              onReject={onRejectSuggestion}
            />
          </section>
        ) : null}

        {!draft.barcode || !validPrice ? (
          <section className="scan-extra-captures">
            <h3>Ajouter une photographie secondaire</h3>
            <div className="scan-extra-capture-actions">
              {draft.barcode ? null : (
                <ScanIconButton label="Photographier le code-barres" caption="Code-barres" onClick={() => onAdditionalPhoto('barcode')}>
                  <IconBarcode />
                </ScanIconButton>
              )}
              {validPrice ? null : (
                <ScanIconButton label="Photographier le prix" caption="Prix" onClick={() => onAdditionalPhoto('price')}>
                  <IconPriceTag />
                </ScanIconButton>
              )}
            </div>
          </section>
        ) : null}

        <p className="scan-product-hint">Vérifiez et confirmez les informations</p>
        {draft.pageTitle && draft.pageTitle !== draft.name ? (
          <p className="scan-product-hint">Titre de la page source : {draft.pageTitle}</p>
        ) : null}
        {draft.cropPreviewUrl ? (
          <figure className="scan-crop-sent">
            <img src={draft.cropPreviewUrl} alt="" />
            <figcaption>Recadrage réellement envoyé à la recherche</figcaption>
          </figure>
        ) : null}
        {draft.needsDetailPhoto ? (
          <p className="scan-product-hint" role="status">
            Plusieurs modèles restent possibles. Photographiez le nom du modèle ou le bas du boîtier pour départager.
          </p>
        ) : null}
        {draft.missingOfficialImage ? (
          <p className="scan-product-hint" role="status">
            Aucun visuel officiel correspondant. La photographie originale est conservée.
          </p>
        ) : null}
        <div className="scan-field">
          <label htmlFor="scan-name">Nom</label>
          <input
            id="scan-name"
            value={draft.name}
            autoComplete="off"
            onChange={(event) => onChange({ name: event.target.value, translatedName: event.target.value })}
          />
        </div>
        <div className="scan-field">
          <label htmlFor="scan-brand">Marque</label>
          <input id="scan-brand" value={draft.brand} onChange={(event) => onChange({ brand: event.target.value })} />
        </div>
        <div className="scan-field-row">
          <div className="scan-field">
            <label htmlFor="scan-model">Modèle</label>
            <input id="scan-model" value={draft.model} onChange={(event) => onChange({ model: event.target.value })} />
          </div>
          <div className="scan-field">
            <label htmlFor="scan-variant">Variante</label>
            <input id="scan-variant" value={draft.variant} onChange={(event) => onChange({ variant: event.target.value })} />
          </div>
        </div>
        <div className="scan-field">
          <label htmlFor="scan-category">Catégorie</label>
          <input id="scan-category" value={draft.category} onChange={(event) => onChange({ category: event.target.value })} />
        </div>
        <div className="scan-field">
          <label htmlFor="scan-barcode">Code-barres</label>
          <input id="scan-barcode" value={draft.barcode} onChange={(event) => onChange({ barcode: event.target.value })} />
        </div>
        <div className="scan-field">
          <label htmlFor="scan-price">{currency === 'EUR' ? 'Prix constaté (EUR)' : 'Prix constaté (JPY)'}</label>
          <div className="scan-price-input">
            <span aria-hidden="true">{currency === 'EUR' ? '€' : '¥'}</span>
            <input
              ref={priceRef}
              id="scan-price"
              inputMode="decimal"
              placeholder={currency === 'EUR' ? 'Ex. 49,99' : 'Ex. 1 280'}
              value={draft.price}
              onChange={(event) => onChange({ price: event.target.value, convertedPrice: null })}
            />
          </div>
          {(draft.priceCandidates || []).length ? (
            <div className="scan-price-candidates" aria-label="Prix détectés">
              {(draft.priceCandidates || []).slice(0, 4).map((candidate, index) => {
                const amount = typeof candidate === 'object'
                  ? candidate.amount ?? candidate.amount_jpy ?? candidate.value
                  : candidate
                const taxLabel = typeof candidate === 'object' ? candidate.taxLabel || candidate.tax : ''
                const frenchTax = String(taxLabel).includes('込') || candidate?.taxIncluded === true
                  ? 'incluse'
                  : String(taxLabel).includes('抜') || candidate?.taxIncluded === false
                    ? 'hors taxe'
                    : ''
                return (
                  <button key={`${amount}-${index}`} type="button" className="scan-chip" onClick={() => onChange({
                    currency: 'JPY',
                    price: String(amount),
                    taxStatus: candidate?.taxIncluded === true ? 'included' : candidate?.taxIncluded === false ? 'excluded' : '',
                    convertedPrice: null,
                  })}>
                    ¥{Number(amount).toLocaleString('fr-FR')}{frenchTax ? ` · ${frenchTax}` : ''}
                  </button>
                )
              })}
            </div>
          ) : null}
          {currency === 'JPY' && Number(draft.convertedPrice) > 0 ? (
            <p className="scan-product-hint">
              Conversion indicative : {Number(draft.convertedPrice).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
            </p>
          ) : null}
        </div>
        <div className="scan-field-row">
          <div className="scan-field">
            <label htmlFor="scan-currency">Devise</label>
            <ScanGlassSelect
              id="scan-currency"
              label="Devise"
              value={currency}
              options={CURRENCY_OPTIONS}
              onChange={changeCurrency}
            />
          </div>
          <div className="scan-field">
            <label htmlFor="scan-tax">Taxe</label>
            <ScanGlassSelect
              id="scan-tax"
              label="Taxe"
              value={draft.taxStatus}
              options={TAX_OPTIONS}
              onChange={(taxStatus) => onChange({ taxStatus })}
            />
          </div>
        </div>
        <div className="scan-field">
          <label htmlFor="scan-qty">Quantité</label>
          <input
            id="scan-qty"
            inputMode="numeric"
            value={draft.quantity}
            onChange={(event) => onChange({ quantity: event.target.value })}
          />
        </div>
        <div className="scan-field">
          <label htmlFor="scan-observed">Date de constatation</label>
          <input
            id="scan-observed"
            type="datetime-local"
            value={draft.observedAt}
            onChange={(event) => onChange({ observedAt: event.target.value })}
          />
        </div>
        <ScanStorePicker {...storeState} />
        {error ? (
          <div className="scan-save-error" role="alert">
            <p className="scan-product-hint">{error}</p>
            {error === 'Enregistrement impossible' ? (
              <div className="scan-save-error-actions">
                <button type="button" className="scan-ghost" onClick={onSubmit}>Réessayer</button>
                <button type="button" className="scan-ghost" onClick={() => priceRef.current?.focus()}>Modifier</button>
              </div>
            ) : null}
          </div>
        ) : null}
        {draft.captureUploadStatus === 'failed' ? (
          <p className="scan-product-hint" role="status">Photographie non enregistrée — nouvelle tentative à l’ajout.</p>
        ) : null}
      </div>
      <div className="scan-review-actions">
        <button type="button" className="scan-secondary scan-review-retake" onClick={onRetake}>
          <IconCameraRetake />
          <span>Reprendre la photo</span>
        </button>
        <button type="button" className="scan-primary" onClick={onSubmit} disabled={submitting || !canSubmit} aria-label="Ajouter au panier">
          <IconCartPlus />
          <span>{submitting ? 'Ajout…' : 'Ajouter au panier'}</span>
        </button>
      </div>
    </>
  )
}
